import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { ArrowRight, CalendarDays, Timer } from "lucide-react";
import { apiAssetUrl, getPublicCohorts } from "@/lib/api";

const day = (value) => new Date(`${value}T00:00:00`).toLocaleDateString(undefined, { day: "numeric", month: "short" });
// How long each open cohort stays in view before the card moves on to the next.
const ROTATE_MS = 3000;

// Every cohort the landing page can advertise: joinable ones, featured first (the API orders them).
export function useOpenCohorts() {
  const [cohorts, setCohorts] = useState([]);
  useEffect(() => {
    let active = true;
    getPublicCohorts()
      .then((result) => { if (active) setCohorts((result.items || []).filter((item) => item.joinable)); })
      .catch(() => {});
    return () => { active = false; };
  }, []);
  return cohorts;
}

export function NextCohortPill({ cohort }) {
  if (!cohort) return null;
  return (
    <Link to={`/cohorts/${cohort.slug}`} data-testid="hero-cohort" className="group mt-6 inline-flex max-w-full items-center gap-3 rounded-full border border-[#F59E0B]/30 bg-[#F59E0B]/[0.06] py-1.5 pl-1.5 pr-4 text-xs text-[#D4D4D8] transition-colors hover:border-[#F59E0B]/60">
      <span className="rounded-full bg-[#F59E0B] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-black">Cohort</span>
      <span className="truncate">{cohort.name} starts {day(cohort.startsOn)} · {cohort.spotsLeft} spots left</span>
      <ArrowRight className="h-3.5 w-3.5 shrink-0 transition-transform group-hover:translate-x-0.5" />
    </Link>
  );
}

function CohortSlide({ cohort, label }) {
  return (
    <div className={cohort.imageUrl ? "grid gap-8 sm:grid-cols-[200px_1fr]" : ""}>
      {cohort.imageUrl && <img src={apiAssetUrl(cohort.imageUrl)} alt="" loading="lazy" className="mx-auto block aspect-[4/5] w-full max-w-[240px] self-start rounded-md border border-white/10 object-cover sm:mx-0" />}
      <div className="min-w-0">
        <div className="overline mb-4 inline-flex items-center gap-2"><CalendarDays className="h-3.5 w-3.5" /> {label}</div>
        <h2 className="font-serif-editorial text-4xl font-light leading-[1.04] tracking-tight lining-nums sm:text-5xl text-balance">{cohort.name}</h2>
        {cohort.tagline && <p className="mt-4 max-w-lg text-[#D4D4D8]">{cohort.tagline}</p>}
        <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#A1A1AA]">Everyone joins before {day(cohort.startsOn)}. Come alone and the team pairs you by hand on day one, or bring your partner or a friend and start together.</p>
        <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#A1A1AA]">
          <span>{day(cohort.startsOn)} – {day(cohort.endsOn)} · {cohort.lengthDays} days</span>
          <span className="text-[#F5F5F0]">{cohort.spotsLeft} of {cohort.capacity} spots left</span>
          {cohort.niche && <span>{cohort.niche}</span>}
        </div>
        <Link to={`/cohorts/${cohort.slug}`} className="hm-btn-primary mt-8 inline-flex items-center gap-2 rounded-md px-6 py-3.5 text-sm font-semibold">Save my spot <ArrowRight className="h-4 w-4" /></Link>
      </div>
    </div>
  );
}

// Open cohorts take turns in one card. All of them sit in the same grid cell, so the card keeps the
// tallest one's height and the page doesn't jump as they rotate. The active segment's fill is the
// timer: when it finishes, the next cohort comes in. It waits until the card is on screen, and hover or
// keyboard focus pauses it.
function RotatingCohorts({ cohorts }) {
  const reduce = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.5 });
  const [index, setIndex] = useState(0);
  const [hovered, setPaused] = useState(false);
  const paused = hovered || !inView;
  const count = cohorts.length;
  const current = Math.min(index, count - 1);
  const rotating = count > 1 && !reduce;
  const label = count > 1 ? "Open cohorts" : "Next cohort";
  const next = () => setIndex((value) => (value + 1) % count);

  return (
    <div
      ref={ref}
      className="hm-card relative h-full rounded-lg p-8 sm:p-10"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setPaused(false); }}
      aria-roledescription="carousel"
      aria-label="Open cohorts"
      data-testid="cohort-carousel"
    >
      {count > 1 && (
        <div className="absolute right-8 top-8 z-10 flex items-center gap-1.5 sm:right-10 sm:top-10" role="group" aria-label="Choose a cohort">
          {cohorts.map((cohort, position) => (
            <button
              key={cohort.id}
              type="button"
              onClick={() => setIndex(position)}
              aria-label={`Show ${cohort.name}`}
              aria-current={position === current}
              className="relative h-1 w-7 overflow-hidden rounded-full bg-white/15 transition-colors hover:bg-white/25"
            >
              {position === current && (rotating ? (
                <span
                  key={`fill-${current}`}
                  className="cohort-progress absolute inset-0 origin-left rounded-full bg-[#F59E0B]"
                  style={{ animationDuration: `${ROTATE_MS}ms`, animationPlayState: paused ? "paused" : "running" }}
                  onAnimationEnd={next}
                />
              ) : <span className="absolute inset-0 rounded-full bg-[#F59E0B]" />)}
            </button>
          ))}
        </div>
      )}
      <div className="grid">
        {cohorts.map((cohort, position) => {
          const active = position === current;
          return (
            <motion.div
              key={cohort.id}
              className="[grid-area:1/1]"
              initial={false}
              animate={active
                ? { opacity: 1, x: 0, visibility: "visible" }
                : { opacity: 0, x: reduce ? 0 : -16, transitionEnd: { visibility: "hidden" } }}
              transition={{ duration: reduce ? 0 : 0.55, ease: [0.16, 1, 0.3, 1] }}
              aria-hidden={!active}
              role="group"
              aria-roledescription="slide"
              aria-label={`${position + 1} of ${count}`}
              style={{ pointerEvents: active ? "auto" : "none" }}
            >
              <CohortSlide cohort={cohort} label={label} />
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

export function CohortSection({ cohorts = [] }) {
  return (
    <section id="cohorts" data-testid="cohort-section" className="relative border-t border-white/5 py-24 sm:py-32">
      <div className="mx-auto grid max-w-6xl gap-6 px-6 sm:px-10 lg:grid-cols-12">
        <div className="lg:col-span-7">
          {cohorts.length ? (
            <RotatingCohorts cohorts={cohorts} />
          ) : (
            <div className="hm-card h-full rounded-lg p-8 sm:p-10">
              <div className="overline mb-4">Cohorts</div>
              <h2 className="font-serif-editorial text-4xl font-light leading-[1.04] tracking-tight sm:text-5xl">Start on the same day as everyone else.</h2>
              <p className="mt-4 max-w-lg text-sm leading-relaxed text-[#A1A1AA]">Members join a dated cohort and get paired by hand on day one. The next one is announced on the cohorts page first.</p>
              <Link to="/cohorts" className="hm-btn-ghost mt-8 inline-flex items-center gap-2 rounded-md px-5 py-3 text-sm">See cohorts <ArrowRight className="h-4 w-4" /></Link>
            </div>
          )}
        </div>
        <div className="lg:col-span-5">
          <div className="hm-card flex h-full flex-col rounded-lg p-8 sm:p-10">
            <div className="overline mb-4 inline-flex items-center gap-2"><Timer className="h-3.5 w-3.5" /> Free tool</div>
            <h3 className="font-serif-editorial text-3xl font-light leading-tight">A focus timer, no account needed.</h3>
            <p className="mt-3 text-sm leading-relaxed text-[#A1A1AA]">The same split-flap clock members use, free in your browser. Nothing is uploaded.</p>
            <Link to="/focus" data-testid="landing-focus-link" className="hm-btn-ghost mt-auto inline-flex w-fit items-center gap-2 rounded-md px-5 py-3 text-sm sm:mt-8">Start a focus block <ArrowRight className="h-4 w-4" /></Link>
          </div>
        </div>
      </div>
    </section>
  );
}
